/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature';
import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { deleteUserRelationFetcher, selfFetcher, selfUpdater, UPDATE_USER_REGISTRATION_QUERY } from '@/data/Content/_Person';
import { useLocalization } from '@/data/Localization';
import { dFix, useSettings } from '@/data/Settings';
import { DATA_KEY_PERSON } from '@/data/constants/dataKey';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import {
	COOKIE_MARKETING_TRACKING_CONSENT,
	COOKIE_PRIVACY_NOTICE_VERSION,
	MARKETING_TRACKING_CONSENT_KEY,
	PRIVACY_NOTICE_VERSION_KEY,
} from '@/data/constants/privacyPolicy';
import { useCookieState } from '@/data/cookie/useCookieState';
import { TransactionErrorResponse } from '@/data/types/Basic';
import {
	EditablePersonInfo,
	EditablePersonInfoParam,
	Person,
	PersonContact,
	XChildrenDetails,
} from '@/data/types/Person';
import { isMappedAddressInfoArray } from '@/data/utils/contact';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { personalContactInfoMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personalContactInfoMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { isUndefined } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { getChildrenAttrs, initializeXChildrenDetails } from '../utils/personChildrenDetails';

export type { EditablePersonInfo, EditablePersonInfoParam };

export type ChangePasswordValues = {
	xcred_logonPasswordOld: string;
	logonPassword: string;
	xcred_logonPasswordVerify: string;
};

const dataMap = (data?: Person, storeId?: string): EditablePersonInfo => {
	const {
		firstName = '',
		lastName = '',
		email1 = '',
		addressLine = ['', ''],
		city = '',
		country = 'NL',
		state = '',
		zipCode = '',
		phone1 = '',
		gender = 'U',
		dateOfBirth = '',
		orgizationId: parentOrgId,
		companyName: companyName,
		receiveEmailPreference,
		children = 0,
		x_childrenDetails:childreDetails
	} = data ?? {};

	let genderMapped = gender;
	if(gender === 'Unspecified')genderMapped = 'U';
	else if(gender === 'Male')genderMapped = 'M';
	else if(gender === 'Female')genderMapped = 'F';

	const dateOfBirthD = new Date(dateOfBirth);
	const isDOBValid = !isNaN(dateOfBirthD.getTime());
	const {
		birth_year,
		birth_month,
		birth_date,
	} = {
		birth_year:isDOBValid?dateOfBirthD.getFullYear().toString():'',
		birth_month:isDOBValid?((dateOfBirthD.getMonth())+1).toString():'',
		birth_date:isDOBValid?dateOfBirthD.getDate().toString():'',
	}
	const dateOfBirthT = isDOBValid?birth_year + '-' + birth_month + '-' + birth_date:'';

	const marketingTrackingConsentValue = data?.contextAttribute
		?.find((attribute) => attribute.attributeName === MARKETING_TRACKING_CONSENT_KEY)
		?.attributeValue?.find((value) => value.storeId === storeId)?.value[0];

	let x_childrenDetails: XChildrenDetails[] = [];
	if (childreDetails) {
		try {
			x_childrenDetails = JSON.parse(childreDetails) as XChildrenDetails[];
		} catch (error) {
			console.error("Error parsing x_childrenDetails:", error);
		}
	}

	return {
		firstName,
		lastName,
		email1,
		addressLine1: addressLine[0] ?? '',
		addressLine2: addressLine[1] ?? '',
		addressLine3: addressLine[2] ?? '',
		city,
		country:'NL',
		state,
		zipCode,
		phone1,
		parentOrgId,
		companyName,
		demographicField7: data?.attributes?.find(o => o.pProfileAttrKey === 'taxPayerId')?.pProfileAttrValue,
		taxPayerId: data?.attributes?.find(o => o.pProfileAttrKey === 'demographicField7')?.pProfileAttrValue,
		privacyNoticeVersion: data?.contextAttribute
			?.find((attribute) => attribute.attributeName === PRIVACY_NOTICE_VERSION_KEY)
			?.attributeValue?.find((value) => value.storeId === storeId)?.value[0],
		...(marketingTrackingConsentValue && {
			marketingTrackingConsent: marketingTrackingConsentValue === '1',
		}),
		gender:genderMapped,
		dateOfBirth:dateOfBirthT,
		birth_year,
		birth_month,
		birth_date,
		receiveEmailPreference,
		children,
		children_selection:children && children>0 ? "YES":"NO",
		x_childrenDetails:initializeXChildrenDetails(children, x_childrenDetails, "DD-MM-YYYY")
	};
};

export const usePersonInfo = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const params = useExtraRequestParameters();
	const personalInformationNLS = useLocalization('PersonalInformation');
	const { showSuccessMessage, notifyError } = useNotifications();
	const [editing, setEditing] = useState(false);
	const [changePassword, setChangePassword] = useState(false);
	const { data: _marketingConsent } = useFlexFlowStoreFeature({
		id: EMS_STORE_FEATURE.MARKETING_CONSENT,
	});
	const { featureEnabled: marketingConsentFeature } = _marketingConsent;

	const {
			defaultCatalogId: catalogId,
		} = getClientSideCommon(settings, router);

	const { data: sessionFeature } = useFlexFlowStoreFeature({ id: EMS_STORE_FEATURE.SESSION });
	const isSession = sessionFeature.featureEnabled;
	const [privacyNoticeVersion, setPrivacyPolicyVersion] = useCookieState<number>(
		COOKIE_PRIVACY_NOTICE_VERSION,
		isSession
	);
	const [_, setMarketingTrackingConsent] = useCookieState<number>(
		COOKIE_MARKETING_TRACKING_CONSENT,
		isSession
	);

	const {
		data,
		error: personInfoError,
		mutate: mutatePersonInfo,
	} = useSWR(
		settings?.storeId
			? [
					{
						storeId: settings.storeId,
					},
					DATA_KEY_PERSON,
			  ]
			: null,
		async ([props]) => selfFetcher(true)(props.storeId, {accessProfile:"Intertoys_Get_UserRelations"}, params)
	);
	
	const edit = useCallback(() => {
		setEditing(true);
	}, []);

	const cancelEdit = useCallback(async () => {
		setEditing(false);
	}, []);

	const savePersonInfo = useCallback(
		async (info: EditablePersonInfo) => {
			const {
				addressLine1,
				addressLine2,
				addressLine3,
				marketingTrackingConsent: _consent,
				privacyNoticeVersion: _discard,
				demographicField7,
				taxPayerId,
				children,
				x_childrenDetails,
				email1,
				..._address
			} = info;
			const privacyData = {
				...(!isUndefined(_consent) && { marketingTrackingConsent: _consent ? '1' : '0' }),
				...(!isUndefined(privacyNoticeVersion) && { privacyNoticeVersion }),
			};
			const storeId = settings?.storeId ?? '';
			const attributes = getChildrenAttrs(x_childrenDetails??[])??[];
			const data = <EditablePersonInfoParam>{
				addressLine : [addressLine1, addressLine2,addressLine3],
				address1: addressLine1,
				address2: addressLine2 ?? '',
				address3: addressLine3 ?? '',	
				demographicField7,
				taxPayerId,
				email1,
				..._address,
				...privacyData,
				...(attributes.length > 0 && { attributes }),
				logonIdEmail: email1,
				storeId:storeId,
				catalogId:catalogId,
				children: children?.toString()??'0'
			};
			delete data.state;
			
			try {
				const resp = await selfUpdater(true)(storeId, UPDATE_USER_REGISTRATION_QUERY, data, params);
				if (isMappedAddressInfoArray(resp)) {
					return {
						validatedAddresses: resp,
						editingAddress: info,
						callback: cancelEdit,
					};
				}
				mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
				setEditing(false);
				privacyData.privacyNoticeVersion &&
					setPrivacyPolicyVersion(dFix(privacyData.privacyNoticeVersion));
				privacyData.marketingTrackingConsent &&
					setMarketingTrackingConsent(dFix(privacyData.marketingTrackingConsent));
				showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[
			privacyNoticeVersion,
			settings?.storeId,
			params,
			setPrivacyPolicyVersion,
			setMarketingTrackingConsent,
			showSuccessMessage,
			personalInformationNLS.UpdateSuccessful,
			cancelEdit,
			notifyError,
		]
	);

	const deleteUserRelation = useCallback(
		async(userRelId:string|null) => {
		if(!userRelId)return;
		const storeId = settings?.storeId ?? '';
		try {
			await deleteUserRelationFetcher(true)(storeId, userRelId, params);
			mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
			showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		}
	},[])

	const closePasswordDialog = useCallback(() => {
		setChangePassword(false);
	}, []);

	const openPasswordDialog = useCallback(() => {
		setChangePassword(true);
	}, []);

	const updatePassword = useCallback(
		async (value: ChangePasswordValues) => {
			const data = { resetPassword: 'true', ...value };
			const storeId = settings?.storeId ?? '';
			try {
				await selfUpdater(true)(storeId, undefined, data, params);
				setChangePassword(false);
				showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[
			notifyError,
			personalInformationNLS.UpdateSuccessful,
			settings?.storeId,
			showSuccessMessage,
			params,
		]
	);

	const { personInfo, primaryAddress } = useMemo(() => {
		const personInfo = dataMap(data, settings?.storeId);
		if (marketingConsentFeature && personInfo.marketingTrackingConsent === undefined) {
			personInfo.marketingTrackingConsent = false;
		}
		const { nickName, addressLine, addressType, addressId } = data ?? {};
		const primaryAddress = nickName
			? ({
					...personInfo,
					addressId,
					addressLine,
					nickName,
					addressType,
					primary: 'true',
			  } as PersonContact)
			: undefined;
		return { personInfo, primaryAddress };
	}, [marketingConsentFeature, data, settings?.storeId]);

	return {
		personInfo,
		primaryAddress,
		mutatePersonInfo,
		personInfoError,
		editing,
		edit,
		cancelEdit,
		savePersonInfo,
		updatePassword,
		closePasswordDialog,
		openPasswordDialog,
		changePassword,
		deleteUserRelation
	};
};